// ======================================================
// 🧮 Calculation Utilities (Refactored & Optimized)
// ======================================================

import {
  isOriginationRow,
  calcOriginationDollarValue,
  getOwnRowPercent,
} from "../../../../utils/originationFee";

// ---------- Basic Helpers ----------

const parseCurrency = (val) => {
  const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toFixed2 = (num) => Number((num ?? 0).toFixed(2));

const safeDivide = (a, b) => (b ? a / b : 0);

/**
 * Amount the Buyer actually owes for a title/escrow-style fee, given who pays it — same
 * Buyer/Seller/Split convention as Owners/Lenders Title Policy and Escrow or Settlement in
 * EscrowTitleFields.js: Seller -> 0 (buyer doesn't pay it), Buyer -> the full fee, Split ->
 * half. An unset payor ("" — e.g. a transfer-tax row the backend couldn't resolve a customary
 * payor for) shows the full amount so the total isn't silently short until the user picks one.
 */
export const computeTitleFeePayorAmount = (payor, baseAmount) => {
  const amount = Number(baseAmount) || 0;
  const p = String(payor || "").toLowerCase();
  if (p === "seller") return 0;
  if (p === "split") return amount / 2;
  return amount; // "buyer" or unset
};

// ---------- Individual Calculators ----------

const calcPrepaidMonthTaxes = (salesPrice, rate, monthsIns) =>
  safeDivide(salesPrice * rate * monthsIns, 1200);

const calcMonthInsurance = (salesPrice, rateThou, monthsIns, loanType) => {
  if (loanType === "Cash") return 0;
  if (monthsIns > 0) return (salesPrice * rateThou * monthsIns) / 12000;
  return 0;
};

const calcDaysInterestAmount = (amount, adjusted, rate, daysInterest) =>
  safeDivide((adjusted || amount) * rate * daysInterest, 36000);

const calcPrincipalAndInterest = (amount, rate, years) => {
  if (amount <= 0 || rate <= 0) return 0;
  if (years <= 0) return (amount * rate) / 1200;
  const r = rate / 1200;
  const n = years * 12;
  const factor = Math.pow(1 + r, n);
  return (amount * r * factor) / (factor - 1);
};

const calcHomeOwnerInsurance = (tax, monthsIns) => safeDivide(tax, monthsIns);
const calcRealEstateTaxes = (tax, monthsTaxes) => safeDivide(tax, monthsTaxes);

// ======================================================
// 🔹 Prepaid Payment Calculation
// ======================================================

export const getDaysInterestAmount = (amount, adjusted, rate, daysInterest) =>
  calcDaysInterestAmount(amount, adjusted, rate, daysInterest);

export const getPrepaidMonthTaxes = (salesPrice, rate, monthsTaxes) =>
  calcPrepaidMonthTaxes(salesPrice, rate, monthsTaxes);

export const getMonthInsurance = (salesPrice, rateThou, monthsIns, loanType) =>
  calcMonthInsurance(salesPrice, rateThou, monthsIns, loanType);

export const getPrepaidPaymentData = (formData = {}, result = {}) => {
  const fd = formData || {};
  const meta = fd.loanInfoMeta || {};
  const prepaids = fd.prepaidsMeta || {};

  const salesPrice = parseCurrency(fd.salesPrice);
  const amount = parseCurrency(result.amount);
  const adjusted = parseCurrency(result.adjusted);
  const rate = parseCurrency(prepaids.rate);
  const rateThou = parseCurrency(prepaids.rateThou);
  const monthsIns = parseCurrency(prepaids.monthsIns);
  const monthsTaxes = parseCurrency(prepaids.monthsTaxes);
  const daysInterest = parseCurrency(prepaids.daysInterest);
  const interestRate = parseCurrency(meta.interestRate1);
  const terms = parseCurrency(meta.termsInYears1);
  const loanType = meta.buyersLoanType;
  const amount2 = parseCurrency(result.amount2);
  const interestRate2 = parseCurrency(meta.interestRate2);
  const terms2 = parseCurrency(meta.termsInYears2);


  // console.log("Sales Price: " + salesPrice);
  // console.log("rate: " + rate);
  // console.log("monthsTaxes: " + monthsTaxes);
  // console.log("prepaids.useForPrepaidCheck: " + meta.useForPrepaidCheck);
  // console.log("getPrepaidPaymentData() params:", { salesPrice, amount, adjusted, daysInterest, interestRate, terms, loanType });
  const prepaidMonthTaxes = meta.useForPrepaidCheck === true
    ? getAnnualTaxforPrepaid(fd)
    : calcPrepaidMonthTaxes(salesPrice, rate, monthsTaxes);

  // console.log("prepaidMonthTaxes : ", prepaidMonthTaxes);
  const monthInsurance = calcMonthInsurance(salesPrice, rateThou, monthsIns, loanType);
  let daysInterestAmount = calcDaysInterestAmount(amount, adjusted, interestRate, daysInterest);
  if (amount2 > 0 && interestRate2 > 0) {
    daysInterestAmount += calcDaysInterestAmount(amount2, 0, interestRate2, daysInterest);
  }
  const principalAndInterest = calcPrincipalAndInterest(adjusted || amount, interestRate, terms);
  const homeOwnerInsuranceRes = calcHomeOwnerInsurance(monthInsurance, monthsIns);
  const realEstateTaxes = calcRealEstateTaxes(prepaidMonthTaxes, monthsTaxes);
  const pAndI2ndTDAmount =
    amount2 > 0 && interestRate2 > 0 && terms2 > 0
      ? calcPrincipalAndInterest(amount2, interestRate2, terms2)
      : 0;

  // console.log("getPrepaidPaymentData() result:", { prepaidMonthTaxes, monthInsurance, daysInterestAmount, principalAndInterest, homeOwnerInsuranceRes, realEstateTaxes });
  return {
    prepaidMonthTaxes: toFixed2(prepaidMonthTaxes),
    monthInsurance: toFixed2(monthInsurance),
    daysInterestAmount: toFixed2(daysInterestAmount),
    principalAndInterest: toFixed2(principalAndInterest),
    homeOwnerInsuranceRes: toFixed2(homeOwnerInsuranceRes),
    realEstateTaxes: toFixed2(realEstateTaxes),
    pAndI2ndTDAmount: toFixed2(pAndI2ndTDAmount),
  };
};

// ======================================================
// 🔹 Second Trust Deed Calculation
// ======================================================

export const getPSecondTd = (formData = {}, result = {}) => {
  const meta = formData.loanInfoMeta || {};
  const amount = parseCurrency(result.amount2);
  const rate = parseCurrency(meta.interestRate2);
  const years = parseCurrency(meta.termsInYears2);

  if (!amount || !rate || !years) return { pAndI2ndTDAmount: "0.00" };

  const r = rate / 1200;
  const n = years * 12;
  const factor = Math.pow(1 + r, n);
  const val = (amount * r * factor) / (factor - 1);

  return { pAndI2ndTDAmount: toFixed2(val) };
};

// ======================================================
// 🔹 Two Month PMI Calculation
// ======================================================

// Recomputes rateMonthlyPMIAmount/twoMonthsPMI from a user-overridden rateMonthlyPMI.
// Same (loanAmount * rateValue) / 1200 formula for every loan type — matches getTwoMonthPMI's math.
export const getMonthlyPMIAmount = (formData) => {
  let rateMonthlyPMI = parseCurrency(formData?.paymentMeta?.rateMonthlyPMI);
  let totalAmount = parseCurrency(formData?.loanInfoMeta?.amount) + parseCurrency(formData?.loanInfoMeta?.amount2);
  let rateMonthlyPMIAmount = safeDivide(totalAmount * rateMonthlyPMI, 1200);
  let twoMonthsPMI = rateMonthlyPMIAmount * 2;

  return {
    rateMonthlyPMIAmount: toFixed2(rateMonthlyPMIAmount),
    rateMonthlyPMI: rateMonthlyPMI,
    twoMonthsPMI: toFixed2(twoMonthsPMI),
  };
}

export const getAnnualTaxforPrepaid = (formData = {}) => {
  const loan = formData.loanInfoMeta || {};
  const prep = formData.prepaidsMeta || {};
  const annual = parseCurrency(loan.annualPropertyTax);
  if (annual > 0) {
    return (annual / 12) * parseCurrency(prep.monthsTaxes);
  }
  return getPrepaidMonthTaxes(
    parseCurrency(formData.salesPrice),
    prep.rate,
    parseCurrency(prep.monthsTaxes)
  );
};

export const getTwoMonthPMI = (formData = {}, templateformData = {}, result = {}) => {
  // console.log("getTwoMonthPMI() called with:", { formData, templateformData, result });
  let rateValue = 0;
  let rateMonthlyPMIAmount = 0.00;
  let rateMonthly = 0.00;
  let salesPrice = parseCurrency(formData?.salesPrice);
  let loanType = formData?.loanInfoMeta?.buyersLoanType || "Conventional";
  // console.log("getTwoMonthPMI() loanType:", loanType);

  if (loanType === "Conventional" || loanType === "Owner Carry") {
    const conv = templateformData?.dataNational?.conventional_loans;
    if (!conv) {
      return { rateMonthlyPMI: 0, rateMonthlyPMIAmount: "0.00", twoMonthsPMI: "0.00" };
    }

    const loan = formData.loanInfoMeta || {};
    const percent1 = parseCurrency(loan.conventionalPercent);
    const percent2 = parseCurrency(loan.conventionalPercent2);
    const totalPercent = percent1 + percent2;
    const years = parseCurrency(loan.termsInYears1) + parseCurrency(loan.termsInYears2);
    const totalAmount = parseCurrency(result.amount) + parseCurrency(result.amount2);

    const rates =
      years > 15
        ? conv?.pmi_rates_30_year?.renewal_rates_percent
        : conv?.pmi_rates_15_year?.renewal_rates_percent;

    if (!rates) {
      return { rateMonthlyPMI: 0, rateMonthlyPMIAmount: "0.00", twoMonthsPMI: "0.00" };
    }


    if (totalPercent > 95 && totalPercent <= 97) rateValue = rates["95_to_97"];
    else if (totalPercent > 90 && totalPercent <= 95) rateValue = rates["90_1_to_95"];
    else if (totalPercent > 85 && totalPercent <= 90) rateValue = rates["85_1_to_90"];
    else if (totalPercent > 80 && totalPercent <= 85) rateValue = rates["80_1_to_85"];


    // console.log("getTwoMonthPMI() params:", { totalPercent, years, totalAmount, rateValue });
    const twoMonthsPMI = safeDivide(totalAmount * rateValue, 1200);
    rateMonthlyPMIAmount = twoMonthsPMI;
    rateMonthly = twoMonthsPMI * 2;
    rateValue = toFixed2(rateValue);

  } else if (loanType === "FHA") {
  } else if (loanType === "VA") {
  } else if (loanType === "Cash") {
  } else if (loanType === "Owner Carry") {
  } else if (loanType === "USDA") {
    let monthly_mip_factor = templateformData?.dataNational?.usda_closing_costs?.loan_amount_factor?.monthly_mip_factor_percent;
    // console.log("getTwoMonthPMI() USDA monthly_mip_factor:", monthly_mip_factor);
    //console.log("getTwoMonthPMI() USDA salesPrice:", salesPrice);
    rateValue = Number((monthly_mip_factor / 100).toFixed(5));
    rateMonthlyPMIAmount = (salesPrice * rateValue * 100) / 1200;
    // console.log("getTwoMonthPMI() USDA rateMonthlyPMIAmount:", rateMonthlyPMIAmount);
    // console.log("getTwoMonthPMI() USDA rateValue:", rateValue);
  }

  return {
    rateMonthlyPMI: rateValue,
    rateMonthlyPMIAmount: toFixed2(rateMonthlyPMIAmount),
    twoMonthsPMI: toFixed2(rateMonthly),
  };


};

// ======================================================
// 🔹 Conventional Loan Amount Calculation
// ======================================================

/**
 * Suggested loan amount for a sales price at a given (admin-configured) LTV
 * percent — e.g. $700,000 at 90% LTV -> $630,000. This is the same first
 * step getAmountConventional's own calculation always starts from; pulled
 * out so any other "auto-suggest a loan amount" entry point (e.g. the
 * Guided flow's Loan Amount question) computes it identically rather than
 * duplicating the formula and risking the two drifting apart.
 */
export const computeConventionalLoanAmount = (salesPrice, conventionalPercent) => {
  const amount = (parseCurrency(salesPrice) * parseCurrency(conventionalPercent)) / 100;
  return parseFloat(amount).toFixed(2);
};

export const getAmountConventional = (formData) => {
  // console.log("getAmountConventional() formData:", formData);
  let amount = parseCurrency(
    computeConventionalLoanAmount(
      formData.salesPrice,
      formData.loanInfoMeta.conventionalPercent,
    ),
  );
  let downPayment = formData.salesPrice;
  let amount2 = 0;
  let ltv1 = formData.loanInfoMeta.conventionalPercent;
  let ltv2 = formData.loanInfoMeta.conventionalPercent2;
  if (ltv2 > 0) {
    amount2 = (parseCurrency(formData.salesPrice) * ltv2) / 100; //Formula applied here to calculate the amount
    downPayment = parseCurrency(downPayment) - parseCurrency(amount2); //calculating down payment
  }

  downPayment = parseCurrency(downPayment) - parseCurrency(amount); //calculating down payment

  if (parseCurrency(formData.loanInfoMeta.requestedDownPayment) > 0) {
    downPayment = parseCurrency(formData.loanInfoMeta.requestedDownPayment);
    amount = parseCurrency(formData.salesPrice) - downPayment;
    if (ltv2 > 0) {
      amount = amount - amount2;
    }
    ltv1 = safeDivide(amount * 100, parseCurrency(formData.salesPrice));
    // console.log("[loanAmountFlow] getAmountConventional: requestedDownPayment override path", {
    //   salesPrice: formData.salesPrice,
    //   requestedDownPayment: formData.loanInfoMeta.requestedDownPayment,
    //   recomputedAmount: amount,
    //   recomputedLtv1: ltv1,
    // });
  } else {
    // console.log("[loanAmountFlow] getAmountConventional: default LTV path", {
    //   salesPrice: formData.salesPrice,
    //   conventionalPercent: formData.loanInfoMeta.conventionalPercent,
    //   amount,
    // });
  }

  // console.log("getAmountConventional() formData:", formData);
  return {
    amount: parseFloat(amount).toFixed(2),
    amount2: parseFloat(amount2).toFixed(2),
    downPayment: parseFloat(downPayment).toFixed(2),
    ltv1: parseFloat(ltv1).toFixed(2),
    ltv2: parseFloat(ltv2).toFixed(2),
  };
};

//  Owner Carry calculation
export const getAmountOwnerCarry = (formData) => {
  let amount = (parseCurrency(formData.salesPrice) * parseCurrency(formData.loanInfoMeta.conventionalPercent)) / 100;
  let amount2 = (parseCurrency(formData.salesPrice) * parseCurrency(formData.loanInfoMeta.conventionalPercent2)) / 100;

  let downPayment = formData.salesPrice - amount - amount2;
  return {
    amount: parseFloat(amount).toFixed(2),
    amount2: parseFloat(amount2).toFixed(2),
    downPayment: parseFloat(downPayment).toFixed(2),
    monthsTaxes: "0",
    taxesAmount: "0.00",
    daysInterestAmount: "0.00",
    twoMonthsPMI: "0.00",
    realEstateTaxes: "0.00",
    pAndI2ndTDAmount: "0.00",
    rateMonthlyPMI: "0.00",
    rateMonthlyPMIAmount: "0.00",
  };
}
// USDA Calculation
export const getAmountUSDA = (formData, templateformData) => {
  let salesPrice = parseCurrency(formData?.salesPrice) || 0;

  let usdaCosts = templateformData?.dataNational?.usda_closing_costs || {};
  let loanFactor = usdaCosts?.loan_amount_factor || {};

  let funding_fee_percent =
    parseCurrency(usdaCosts?.mip_factor_percent) ||
    parseCurrency(loanFactor?.mip_factor_percent) ||
    0;

  let UsdaMipFinance = (salesPrice * funding_fee_percent);

  // Create formatted values
  const UsdaMipFinanceFormatted = UsdaMipFinance.toFixed(2);
  const [wholePart] = UsdaMipFinanceFormatted.split(".");

  // Checked (financing): left box shows the fee, right box (cash-due) is 0,
  // and the fee gets rolled into adjusted. Unchecked: both boxes show the fee,
  // adjusted stays at salesPrice (nothing financed).
  const mipFinanceCheck = formData?.prepaidsMeta?.mipFinanceCheck === true;
  const mipFinance = UsdaMipFinanceFormatted;
  const mipFinanceAmount = mipFinanceCheck ? "0.00" : UsdaMipFinanceFormatted;

  let adjusted = mipFinanceCheck
    ? salesPrice + parseFloat(mipFinance)
    : salesPrice;

  // Return result object
  return {
    adjusted: parseFloat(adjusted.toFixed(2)),
    amount: salesPrice,
    downPayment: "0.00",
    UsdaMipFinance: parseFloat(UsdaMipFinanceFormatted),
    UsdaMipFinance1: `${wholePart}.00`,
    UsdaMipFinance2: "0.00",
    mipFinance,
    mipFinanceAmount,
  };
};

// VA Calculation
export const getAmountVA = (formData, templateformData) => {
  const salesPrice = parseCurrency(formData.salesPrice);

  // Extract VA Closing Costs Settings
  const vaCosts = templateformData?.dataNational?.va_closing_costs || {};
  const mortgageInsurance = vaCosts?.mortgage_insurance || {};

  const round_down_mip = !!mortgageInsurance.round_down_mip;
  const funding_fee_percent = parseCurrency(mortgageInsurance.funding_fee_percent);
  const finance_funding_fee = formData?.prepaidsMeta?.vaffFinanceCheck === true;

  const request = {
    amount: salesPrice,
    FF: funding_fee_percent,
    VA_RoundDownMIP: round_down_mip ? "Y" : "N",
    isfinanceVAMip: finance_funding_fee,
  };
  // console.log("responsePrepaid request:", request);
  // Calculate Funding Finance
  const responsePrepaid = getVaFundingFinance(request);
  // console.log("responsePrepaid:", responsePrepaid);
  return {
    amount: parseFloat(salesPrice).toFixed(2),
    downPayment: parseFloat(0.00).toFixed(2),
    vaffFinanceRate: parseFloat(funding_fee_percent).toFixed(2),
    adjusted: responsePrepaid.adjusted,
    VaFfFin: responsePrepaid.VaFfFin,
    VaFfFin1: responsePrepaid.VaFfFin1,
    VaFfFin2: responsePrepaid.VaFfFin2,
    cashDue: responsePrepaid.cashDue,
    financedFee: responsePrepaid.financedFee,
  };
};

// ------------------------------
// Helper Function: Funding Finance Logic
// ------------------------------
function getVaFundingFinance(request) {
  let amount = parseFloat(request.amount ?? 0);
  const FF = parseFloat(request.FF ?? 0);

  // Base calculation
  let VaFfFin = (amount * FF) / 100; // funding fee amount
  VaFfFin = parseFloat(VaFfFin).toFixed(2);

  // Split integer and decimal parts
  const res = VaFfFin.split(".");
  let VaFfFin1 = "0.00";
  let VaFfFin2 = "0.00";

  // Round logic (if round down)
  if (request.VA_RoundDownMIP === "Y") {
    VaFfFin1 = res[0] + ".00";
    if (res[1]) VaFfFin2 = "0." + res[1];
  } else {
    VaFfFin2 = VaFfFin;
  }

  // Adjusted amount logic — financing the fee rolls the FULL fee into the loan,
  // leaving nothing cash-due; not financing leaves adjusted unchanged and the
  // full fee due in cash. This is independent of the round-down display split above.
  let adjusted = amount;
  let cashDue = VaFfFin;
  let financedFee = "0.00";
  if (request.isfinanceVAMip === true) {
    adjusted = amount + parseFloat(VaFfFin);
    cashDue = "0.00";
    financedFee = VaFfFin;
  } else {
    VaFfFin1 = "0.00";
    VaFfFin2 = VaFfFin;
    adjusted = amount;
  }

  // Final result
  return {
    adjusted: parseFloat(adjusted).toFixed(2),
    VaFfFin: VaFfFin,
    VaFfFin1: VaFfFin1,
    VaFfFin2: VaFfFin2,
    cashDue: cashDue,
    financedFee: financedFee,
  };
}

// ======================================================
// 🔹 FHA Calculation
// ======================================================

export const getAmountFHA = (formData = {}, templateformData = {}) => {
  try {
    const fha = templateformData?.dataNational?.fha_closing_costs;
    if (!fha) return {};

    const salesPrice = parseCurrency(formData.salesPrice);
    const terms = parseCurrency(formData.loanInfoMeta?.termsInYears1);
    const maxLoan = parseCurrency(templateformData?.dataCounty?.maxloanamount);
    const ranges = fha?.loan_amount_factor?.ranges || [];

    // Step 1: Determine LTV
    let LTV = 96.5;
    const under = ranges.find((r) => r.label === "under");
    const to = ranges.find((r) => r.label === "to");
    const over = ranges.find((r) => r.label === "over");
    if (under && salesPrice <= parseCurrency(under.sale_price)) LTV = under.ltv_percent;
    else if (to && salesPrice <= parseCurrency(to.sale_price)) LTV = to.ltv_percent;
    else if (over && salesPrice > parseCurrency(over.sale_price)) LTV = over.ltv_percent;

    // Step 2: MIP settings
    const mipYears = fha?.mortgage_insurance?.years || [];
    let MIP = 1.75,
      roundDownMIP = false;
    if (mipYears.length) {
      const match = mipYears.find((m) => terms <= m.years) || mipYears[0];
      MIP = match?.percent ?? MIP;
      roundDownMIP = match?.round_down_mip;
    }

    // Step 3: Calculate FHA base loan
    let amount = (salesPrice * LTV) / 100;
    if (maxLoan && amount > maxLoan) amount = maxLoan;

    let adjusted = amount + (amount * MIP) / 100;
    let downPayment = salesPrice - amount;

    if (roundDownMIP) {
      amount = Math.floor(amount);
      adjusted = Math.floor(adjusted);
      downPayment = Math.floor(downPayment);
    }

    // Step 4: MIP finance
    const FhaMipFin = amount * (MIP / 100);
    const FhaMipFinRounded = toFixed2(FhaMipFin);
    const [intPart, decPart = "00"] = FhaMipFinRounded.toString().split(".");
    const FhaMipFin1 = `${intPart}.00`;
    const FhaMipFin2 = `0.${decPart}`;

    // Step 5: MMI rate lookup
    const factors = fha?.mmi_factor_setting || [];
    let rateValue = 0.55;
    factors.forEach((f) => {
      const amt = parseCurrency(amount);
      if (f.up_to_amount && amt <= parseCurrency(f.up_to_amount)) rateValue = f.factor;
      else if (f.exceeding_from_amount && amt >= parseCurrency(f.exceeding_from_amount))
        rateValue = f.factor;
    });

    const interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    const daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);
    const daysInterestAmount = calcDaysInterestAmount(amount, adjusted, interestRate, daysInterest);
    // console.log("calcDaysInterestAmount ", amount, adjusted, daysInterest, interestRate);

    const adjustedAmount = amount + parseCurrency(FhaMipFin2);
    const monthlyRateMMI = (adjustedAmount * rateValue) / 1200;
    const monthlyRate = toFixed2(monthlyRateMMI);
    const monthPmiVal = toFixed2(monthlyRateMMI * 2);

    const principalAndInterest = calcPrincipalAndInterest(adjusted, interestRate, terms);

    return {
      amount: toFixed2(amount),
      adjusted: toFixed2(adjusted),
      downPayment: toFixed2(downPayment),
      LTV,
      MIP,
      roundDownMIP,
      FhaMipFin: FhaMipFinRounded,
      FhaMipFin1,
      FhaMipFin2,
      monthlyRate,
      monthPmiVal,
      rateValue,
      daysInterestAmount,
      principalAndInterest: toFixed2(principalAndInterest),
    };
  } catch (err) {
    console.error("getAmountFHA failed:", err);
    return {};
  }
};

// ======================================================
// 🔹 Buyer Commission
// ======================================================

export const getBuyerCommission = (formData = {}) => {
  const sp = parseCurrency(formData.salesPrice);
  const rate = parseCurrency(formData.buyerBrokerCompensation);
  return toFixed2((sp * rate) / 100);
};

// ======================================================
// 🔹 Totals Computation
// ======================================================

export function computeTotals(data = {}, commissionEnabled = true) {
  // console.log("computeTotals() data:", data);
  const safeNum = (v) => parseCurrency(v);
  const asArray = (v) => (Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : []);

  // const sp = safeNum(data.salesPrice);
  const title = data.titleSettlementMeta || {};
  const prepaids = data.prepaidsMeta || {};
  const pay = data.paymentMeta || {};
  const comp = data.compensationMeta || {};
  const buyerDownPayment = safeNum(data.loanInfoMeta.buyerDownPayment);
  const estimatedTaxProrations = safeNum(pay.estimatedTaxProrations);

  const transferTaxRowsTotal = asArray(title.transferTaxRows).reduce(
    (s, row) => s + safeNum(row?.amount),
    0
  );

  const totalTitleSettlement =
    safeNum(title.ownersTitleAmount) +
    safeNum(title.escrowSettlementAmount) +
    safeNum(title.lendersTitleAmount) +
    safeNum(title.discountAmount) +
    transferTaxRowsTotal;

  const buyerClosingCostsMeta_total = asArray(data.buyerClosingCostsMeta).reduce(
    (s, it) => {
      let val = safeNum(it?.value);
      // ── Origination-factor % → dollar cost fallback ──
      if (isOriginationRow(it) && (it?.unit || "").trim() === "%") {
        val = Number(calcOriginationDollarValue(getOwnRowPercent(it), data.salesPrice));
      }
      return s + val;
    },
    0
  );
  const closingCostsMeta_total = asArray(data.closingCostsMeta).reduce(
    (s, it) => s + safeNum(it?.total),
    0
  );

  const totalCommission = commissionEnabled
    ? safeNum(comp.buyerBrokerCompensationAmount) + safeNum(comp.sellerBrokerCompensationAmount)
    : 0;

  const loanType = data.loanInfoMeta?.buyersLoanType;
  const financedMIBox =
    loanType === "FHA 203b"
      ? safeNum(prepaids.fhaMipAmount)
      : loanType === "VA"
        ? safeNum(prepaids.vaffFinanceAmount)
        : loanType === "USDA"
          ? safeNum(prepaids.mipFinanceAmount)
          : loanType === "Conventional" || loanType === "Owner Carry"
            ? safeNum(prepaids.twoMonthsPMI)
            : 0; // Cash

  const totalPrepaids =
    safeNum(prepaids.taxesAmount) +
    safeNum(prepaids.insAmount) +
    safeNum(prepaids.daysInterestAmount) +
    financedMIBox +
    safeNum(prepaids.costsAmount);

  const totalMonthlyPayment =
    safeNum(pay.principalAndInterest) +
    safeNum(pay.realEstateTaxes) +
    safeNum(pay.homeownerInsurance) +
    safeNum(pay.rateMonthlyPMIAmount) +
    safeNum(pay.pAndI2ndTDAmount) +
    safeNum(pay.monthlyExpense1Amount) +
    safeNum(pay.monthlyExpense2Amount);


  const totalClosingCosts =
    closingCostsMeta_total + buyerClosingCostsMeta_total + totalTitleSettlement + totalCommission;

  const totalInvestment = toFixed2(buyerDownPayment + totalClosingCosts + estimatedTaxProrations + totalPrepaids - totalCommission);


  return {
    totalClosingCosts,
    totalCommission,
    totalPrepaids,
    totalMonthlyPayment,
    // Mirrors the just-computed totalMonthlyPayment. Callers stash this as
    // totalClosingMeta.totalMonthlyPaymentOld — the last value the normal
    // (principal+interest+tax+insurance) pipeline actually calculated — kept
    // separate from totalMonthlyPayment itself, which the user can overwrite
    // directly (see recalcSalesPriceFromPayment below).
    totalMonthlyPaymentOld: totalMonthlyPayment,
    totalInvestment,
  };
}

// ======================================================
// 🔹 Reverse-solve Sales Price from an edited Total Monthly Payment
// ======================================================

/**
 * Ported 1:1 from the legacy monthlyPaymentChanged() calculator (including
 * its round-up-to-$250-then-add-another-$250 step, confirmed intentional).
 *
 * Assumes the monthly payment scales ~linearly with sales price (true when
 * loan %, rate, term, tax rate, and insurance rate stay fixed) and back-solves
 * for the sales price that would have produced the newly-typed payment.
 */
export const recalcSalesPriceFromPayment = (
  prevMonthPayment,
  currMonthPayment,
  salesPrice,
) => {
  const sp = parseCurrency(salesPrice);
  if (!sp) return toFixed2(sp).toFixed(2);

  const factor = safeDivide(parseCurrency(prevMonthPayment), sp);
  let newSalesPrice = safeDivide(parseCurrency(currMonthPayment), factor);

  const rem = newSalesPrice % 250;
  if (newSalesPrice !== 0 && rem !== 0) {
    const roundedUp = newSalesPrice + (250 - rem);
    newSalesPrice = roundedUp + 250;
  }

  return newSalesPrice.toFixed(2);
};

// ======================================================
// 🔹 Discount Calculation
// ======================================================

export function calculateDiscount(formData = {}, isDiscountChanged) {
  const meta = formData.titleSettlementMeta || {};
  const discountPercent = parseCurrency(meta.discount);
  const discountAmount = parseCurrency(meta.discountAmount);
  const salesPrice = parseCurrency(formData.salesPrice);

  let computedPercent, computedAmount;

  if (isDiscountChanged) {
    computedPercent = toFixed2(discountPercent);
    computedAmount = toFixed2((salesPrice * discountPercent) / 100);
  } else {
    computedAmount = toFixed2(discountAmount);
    computedPercent = salesPrice ? toFixed2((discountAmount / salesPrice) * 100) : 0;
  }

  return {
    discountPercent: computedPercent,
    discountAmount: computedAmount,
  };
}


// Get Month Info
export function getMonthInfo(month) {
  const monthNames = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const currentMonth = monthNames[month - 1];
  const nextMonth = monthNames[month === 12 ? 0 : month];
  return { currentMonth, nextMonth };
}

// Days until next month
export function calculateDaysInterest(d, m, y) {
  const closingDate = new Date(y, m - 1, d);
  const firstDayNextMonth = new Date(y, m, 1);
  return (firstDayNextMonth - closingDate) / (1000 * 60 * 60 * 24);
}

// Estimated Proration
export function calculateEstimatedProration({
  annualPropertyTax,
  prorations,
  days
}) {
  const perDayTax = annualPropertyTax / 360;
  const daysLeftInMonth = Math.abs(prorations) + days;
  let result = perDayTax * daysLeftInMonth;

  if (prorations < 0) {
    result = -result;
  }

  return {
    estimatedTaxProrations: Number(result.toFixed(2)),
    perDayTax,
    daysLeftInMonth
  };
}


// getBuyerEstimatedTax()
export function getBuyerEstimatedTax(formData = {}, templateformData = {}) {
  const estimatedClosingDate = formData?.loanInfoMeta?.estimatedClosingDate;
  // Use parseCurrency to handle formatted currency strings (e.g., "1,234.56")
  const annualPropertyTax = parseCurrency(formData?.loanInfoMeta?.annualPropertyTax || 0);
  const data1Proration = templateformData?.data1Proration || {};

  if (!estimatedClosingDate || !annualPropertyTax || !Object.keys(data1Proration).length) {
    return { estimatedTaxProrations: 0 };
  }

  // Extract date info
  const { month, days } = getDated(estimatedClosingDate);
  const { currentMonth } = getMonthInfo(month);

  const prorations = parseFloat(data1Proration[currentMonth] || 0);

  // Use helper
  const prorationResult = calculateEstimatedProration({
    annualPropertyTax,
    prorations,
    days
  });


  return {
    ...prorationResult,
  };
}

export function setPrepaidData(formData = {}, dataImpound = {}) {
  const estimatedClosingDate = formData?.loanInfoMeta?.estimatedClosingDate;

  const { dated, month, year } = getDated(estimatedClosingDate);
  const { nextMonth } = getMonthInfo(month);
  const monthsTaxes = parseFloat(dataImpound[nextMonth] || 0);
  const daysInterest = calculateDaysInterest(dated, month, year);

  return {
    monthsTaxes,
    daysInterest,
  };
}


export function resultOfTitleEscrow(dataTitleEscrow) {
  let escrowSettlementAmount = "0.00";
  if (dataTitleEscrow?.data?.escrowType === "Split") {
    escrowSettlementAmount = dataTitleEscrow?.data?.escrowFeeBuyer ?? "0.00";
  } else if (dataTitleEscrow?.data?.escrowType === "Buyer") {
    escrowSettlementAmount = dataTitleEscrow?.data?.escrowFee ?? "0.00";
  }

  let ownersTitleAmount = "0.00";
  if (dataTitleEscrow?.data?.ownerType === "Split") {
    ownersTitleAmount = parseCurrency(dataTitleEscrow?.data?.ownerFee) / 2 ?? "0.00";
  } else if (dataTitleEscrow?.data?.ownerType === "Buyer") {
    ownersTitleAmount = dataTitleEscrow?.data?.ownerFee ?? "0.00";
  }

  let lendersTitleAmount = "0.00";
  if (dataTitleEscrow?.data?.lenderType === "Split") {
    lendersTitleAmount = parseCurrency(dataTitleEscrow?.data?.lenderFee) / 2 ?? "0.00";
  } else if (dataTitleEscrow?.data?.lenderType === "Buyer") {
    lendersTitleAmount = dataTitleEscrow?.data?.lenderFee ?? "0.00";
  }

  return {
    ownersTitleAmount: ownersTitleAmount ?? "0.00",
    ownersTitleBuyer: dataTitleEscrow?.data?.ownerFee ?? "0.00",
    ownersTitlePayor: dataTitleEscrow?.data?.ownerType ?? "Buyer",

    escrowSettlementPayor: dataTitleEscrow?.data?.escrowType ?? "Buyer",
    escrowSettlementAmount: escrowSettlementAmount ?? "0.00",
    escrowSettlementBuyer: dataTitleEscrow?.data?.escrowFee ?? "0.00",
    escrowSettlementSplit: dataTitleEscrow?.data?.escrowFeeBuyer ?? "0.00",

    lendersTitleAmount: lendersTitleAmount ?? "0.00",
    lendersTitleBuyer: dataTitleEscrow?.data?.lenderFee ?? "0.00",
    lendersTitlePayor: dataTitleEscrow?.data?.lenderType ?? "Buyer",
  };

}

export const computeClosingCostTotals = (formData = {}) => {
  const closing = Array.isArray(formData?.closingCostsMeta)
    ? formData?.closingCostsMeta
    : [];

  if (closing.length === 0) {
    return {
      ...formData,
      closingCostsMeta: [],
    };
  }

  const loanType = formData?.loanInfoMeta?.buyersLoanType || "Conventional";
  const sp = parseCurrency(formData?.salesPrice);
  const loan = parseCurrency(formData?.loanInfoMeta?.amount);

 // console.log("computeClosingCostTotals() loanType:", loanType, "sp:", sp, "loan:", loan);
  //console.log("computeClosingCostTotals() closing:", closing);

  const updated = closing.map((cc) => {
    // console.log("computeClosingCostTotals() cc:", cc);
    let rate = parseCurrency(cc.value);
    let total = "0.00";

    if (/%\s*sale/i.test(cc.type)) {
      total = (sp * rate) / 100;
    } else if (/%\s*loan/i.test(cc.type)) {
      total = (loan * rate) / 100;
    } else {
      total = rate;
    }

    // Cash override
    if (loanType === "Cash" && cc.apply === "Y") {
      total = cc.Cash;
      rate = cc.Cash;
    }

    // Direction: a CREDIT row subtracts from the total instead of adding to
    // it (e.g. a seller-paid item entered as a positive magnitude but meant
    // to reduce closing costs). Math.abs first so this stays correct even if
    // rate/total already came out negative above — cc.value/cc.Cash (the
    // source magnitude) are never mutated, so this is safe to recompute
    // every time sales price / loan type changes.
    if (cc.direction === "CREDIT") {
      rate = -Math.abs(parseCurrency(rate));
      total = -Math.abs(parseCurrency(total));
    }

    return {
      ...cc,
      rate: rate,
      total,
    };
  });

  return {
    ...formData,
    closingCostsMeta: updated,
  };
};


function getDated(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return { dated: 0, month: 0, year: 0, days: 0 };

  const dated = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();

  let adjustedDate = dated;
  if (month === 2 && dated >= 28) adjustedDate = 30;
  if (dated === 31) adjustedDate = 30;

  return { dated: adjustedDate, month, year, days: dated };
}
